import React, { useState, useMemo, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import "./DuplicateProjectModal.scss";

/**
 * DuplicateProjectModal
 *
 * Props:
 *  project        – { projectId, projectTitle, description, startDate, endDate }
 *  tasks          – array of task objects (with todoRequestDTOList → toDoListResponseDTOList)
 *  members        – array of project member objects { empId, empName, roleType, roleId }
 *  currentEmpId   – logged-in user's empId (excluded from member copy)
 *  axiosInstance  – configured axios instance
 *  onClose        – fn()
 *  onSuccess      – fn(newProject) called after successful duplication
 *  availableRoles – [{ roleId, roleType }]
 */
export default function DuplicateProjectModal({
  project,
  tasks = [],
  members = [],
  currentEmpId,
  axiosInstance,
  onClose,
  onSuccess,
  availableRoles = [
    { roleId: 1, roleType: "CREATOR" },
    { roleId: 2, roleType: "APPROVER" },
    { roleId: 3, roleType: "DOER" },
    { roleId: 4, roleType: "VIEWER" },
    { roleId: 5, roleType: "CLIENT" },
  ],
}) {
  // ── Step state ──────────────────────────────────────────────────────────────
  const [step, setStep] = useState(1); // 1=details, 2=tasks, 3=members

  // ── Step 1: Project details ─────────────────────────────────────────────────
  const [form, setForm] = useState({
    projectTitle: `${project?.projectTitle || "Project"} (Copy)`,
    description: project?.description || "",
    startDate: project?.startDate || new Date().toISOString().slice(0, 10),
    endDate: project?.endDate || "",
  });

  // ── Step 2: Task/todo/todolist selection ────────────────────────────────────
  const [selectedTasks, setSelectedTasks] = useState(() =>
    buildInitialSelection(tasks),
  );

  // ── Step 3: Members ─────────────────────────────────────────────────────────
  const [copyEmployees, setCopyEmployees] = useState(true);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const otherMembers = useMemo(
    () => members.filter((m) => String(m.empId) !== String(currentEmpId)),
    [members, currentEmpId],
  );

  const [manualMembers, setManualMembers] = useState(() => {
    const init = {};
    members
      .filter((m) => String(m.empId) !== String(currentEmpId))
      .forEach((m) => {
        init[m.empId] = { included: false, roleId: m.roleId || 3 };
      });
    return init;
  });

  // ── Submit state ─────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ── Fetch all employees when step 3 is reached & copyEmployees=false ─────────
  useEffect(() => {
    if (step !== 3 || copyEmployees) return;
    if (allEmployees.length > 0) return;

    const fetchEmployees = async () => {
      setLoadingEmployees(true);
      try {
        const res = await axiosInstance.get("/api/emp/getall");
        const list = res?.data?.data ?? res?.data ?? [];
        const filtered = Array.isArray(list)
          ? list.filter((e) => String(e.empId) !== String(currentEmpId))
          : [];
        setAllEmployees(filtered);
        setManualMembers((prev) => {
          const next = { ...prev };
          filtered.forEach((e) => {
            if (!(e.empId in next)) {
              const existingMember = members.find(
                (m) => String(m.empId) === String(e.empId),
              );
              next[e.empId] = {
                included: false,
                roleId: existingMember?.roleId || 3,
              };
            }
          });
          return next;
        });
      } catch (err) {
        console.error(
          "[DuplicateProjectModal] Failed to fetch employees:",
          err,
        );
      } finally {
        setLoadingEmployees(false);
      }
    };

    fetchEmployees();
  }, [
    step,
    copyEmployees,
    axiosInstance,
    currentEmpId,
    members,
    allEmployees.length,
  ]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  function buildInitialSelection(taskList) {
    const sel = {};
    (taskList || []).forEach((t) => {
      const taskId = t.taskId ?? t.id;
      const todos = {};
      (t.todoRequestDTOList || []).forEach((td) => {
        const todoId = td.toDoId ?? td.id;
        const lists = new Set(
          (td.toDoListResponseDTOList || []).map(
            (li) => li.toDoListId ?? li.id,
          ),
        );
        todos[todoId] = { checked: true, lists };
      });
      sel[taskId] = { checked: true, todos };
    });
    return sel;
  }

  const isDuplicateAll = useMemo(() => {
    for (const task of tasks) {
      const taskId = task.taskId ?? task.id;
      const tsel = selectedTasks[taskId];
      if (!tsel || !tsel.checked) return false;
      for (const todo of task.todoRequestDTOList || []) {
        const todoId = todo.toDoId ?? todo.id;
        const tdsel = tsel.todos[todoId];
        if (!tdsel || !tdsel.checked) return false;
        for (const li of todo.toDoListResponseDTOList || []) {
          const listId = li.toDoListId ?? li.id;
          if (!tdsel.lists.has(listId)) return false;
        }
      }
    }
    return true;
  }, [selectedTasks, tasks]);

  const handleSelectAll = () => setSelectedTasks(buildInitialSelection(tasks));

  const handleDeselectAll = () => {
    setSelectedTasks((prev) => {
      const next = {};
      Object.entries(prev).forEach(([taskId, tv]) => {
        const todos = {};
        Object.entries(tv.todos).forEach(([todoId]) => {
          todos[todoId] = { checked: false, lists: new Set() };
        });
        next[taskId] = { checked: false, todos };
      });
      return next;
    });
  };

  const toggleTask = (taskId) => {
    setSelectedTasks((prev) => {
      const next = { ...prev };
      const cur = next[taskId];
      const newChecked = !cur.checked;
      const origTask = tasks.find(
        (t) => String(t.taskId ?? t.id) === String(taskId),
      );
      const todos = {};
      Object.entries(cur.todos).forEach(([tid, tv]) => {
        const origTodo = (origTask?.todoRequestDTOList || []).find(
          (td) => String(td.toDoId ?? td.id) === String(tid),
        );
        const origLists = new Set(
          (origTodo?.toDoListResponseDTOList || []).map(
            (li) => li.toDoListId ?? li.id,
          ),
        );
        todos[tid] = {
          checked: newChecked,
          lists: newChecked ? origLists : new Set(),
        };
      });
      next[taskId] = { ...cur, checked: newChecked, todos };
      return next;
    });
  };

  const toggleTodo = (taskId, todoId) => {
    setSelectedTasks((prev) => {
      const next = { ...prev };
      const task = { ...next[taskId] };
      const todo = task.todos[todoId];
      const newChecked = !todo.checked;
      const origTask = tasks.find(
        (t) => String(t.taskId ?? t.id) === String(taskId),
      );
      const origTodo = (origTask?.todoRequestDTOList || []).find(
        (td) => String(td.toDoId ?? td.id) === String(todoId),
      );
      const origLists = new Set(
        (origTodo?.toDoListResponseDTOList || []).map(
          (li) => li.toDoListId ?? li.id,
        ),
      );
      task.todos = {
        ...task.todos,
        [todoId]: {
          checked: newChecked,
          lists: newChecked ? origLists : new Set(),
        },
      };
      if (newChecked) task.checked = true;
      next[taskId] = task;
      return next;
    });
  };

  const toggleList = (taskId, todoId, listId) => {
    setSelectedTasks((prev) => {
      const next = { ...prev };
      const task = { ...next[taskId] };
      const todo = { ...task.todos[todoId] };
      const lists = new Set(todo.lists);
      lists.has(listId) ? lists.delete(listId) : lists.add(listId);
      todo.lists = lists;
      if (lists.size > 0) {
        todo.checked = true;
        task.checked = true;
      }
      task.todos = { ...task.todos, [todoId]: todo };
      next[taskId] = task;
      return next;
    });
  };

  const buildPayload = useCallback(() => {
    const base = {
      sourceProjectId: project.projectId,
      projectTitle: form.projectTitle.trim(),
      description: form.description,
      startDate: form.startDate,
      endDate: form.endDate,
      duplicateAll: isDuplicateAll,
      copyEmployees,
    };

    if (!copyEmployees) {
      const memberRoleDTOS = Object.entries(manualMembers)
        .filter(([, v]) => v.included)
        .map(([empId, v]) => ({
          empId: Number(empId),
          roleId: Number(v.roleId),
        }));
      if (memberRoleDTOS.length > 0) base.memberRoleDTOS = memberRoleDTOS;
    }

    if (!isDuplicateAll) {
      const taskIds = [];
      Object.entries(selectedTasks).forEach(([taskId, tv]) => {
        if (!tv.checked) return;
        const todos = [];
        Object.entries(tv.todos).forEach(([todoId, tdv]) => {
          if (!tdv.checked) return;
          const toDoListIds = [...tdv.lists].map((id) => ({ todoListId: id }));
          const todoEntry = { todoId: Number(todoId) };
          if (toDoListIds.length > 0) todoEntry.toDoListIds = toDoListIds;
          todos.push(todoEntry);
        });
        taskIds.push({ taskId: Number(taskId), todos });
      });
      base.taskIds = taskIds;
    }

    return base;
  }, [
    project,
    form,
    isDuplicateAll,
    copyEmployees,
    selectedTasks,
    manualMembers,
  ]);

  const handleSubmit = async () => {
    if (!form.projectTitle.trim()) {
      setError("Project title is required.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload = buildPayload();
      const res = await axiosInstance.post(
        "/api/emp/project/duplicate",
        payload,
        {
          headers: { "Content-Type": "application/json" },
        },
      );
      onSuccess?.(res?.data?.data ?? res?.data ?? null);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to duplicate project",
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedCount = useMemo(() => {
    let t = 0,
      td = 0,
      tl = 0;
    Object.values(selectedTasks).forEach((tv) => {
      if (!tv.checked) return;
      t++;
      Object.values(tv.todos).forEach((tdv) => {
        if (!tdv.checked) return;
        td++;
        tl += tdv.lists.size;
      });
    });
    return { tasks: t, todos: td, lists: tl };
  }, [selectedTasks]);

  const totalCount = useMemo(() => {
    let t = tasks.length,
      td = 0,
      tl = 0;
    tasks.forEach((task) => {
      td += (task.todoRequestDTOList || []).length;
      (task.todoRequestDTOList || []).forEach((todo) => {
        tl += (todo.toDoListResponseDTOList || []).length;
      });
    });
    return { tasks: t, todos: td, lists: tl };
  }, [tasks]);

  const handleNext = () => {
    if (step === 1 && !form.projectTitle.trim()) {
      setError("Project title is required.");
      return;
    }
    setError("");
    setStep((s) => s + 1);
  };

  const STEPS = ["Details", "Tasks & Todos", "Members"];

  const employeesForManual = useMemo(
    () => (allEmployees.length > 0 ? allEmployees : otherMembers),
    [allEmployees, otherMembers],
  );

  const isProjectMember = useCallback(
    (empId) => members.some((m) => String(m.empId) === String(empId)),
    [members],
  );

  return createPortal(
    <div
      className="dup-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Duplicate Project"
      onClick={onClose}
    >
      <div className="dup-modal" onClick={(e) => e.stopPropagation()}>
        {/* ── Header ── */}
        <header className="dup-header">
          <div className="dup-header__title">
            <span className="dup-header__icon" aria-hidden="true">
              ⧉
            </span>
            <h2>Duplicate Project</h2>
          </div>
          <button
            className="dup-close-btn"
            onClick={onClose}
            aria-label="Close"
            title="Close"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </header>

        {/* ── Step indicator ── */}
        <div className="dup-step-bar" role="list" aria-label="Steps">
          {STEPS.map((label, i) => {
            const num = i + 1;
            const isActive = step === num;
            const isDone = step > num;
            return (
              <React.Fragment key={label}>
                <div
                  role="listitem"
                  className={`dup-step-item${isDone ? " dup-step-item--done" : ""}`}
                  onClick={() => isDone && setStep(num)}
                  aria-current={isActive ? "step" : undefined}
                >
                  <span
                    className={[
                      "dup-step-num",
                      isActive ? "dup-step-num--active" : "",
                      isDone ? "dup-step-num--done" : "",
                    ]
                      .join(" ")
                      .trim()}
                  >
                    {isDone ? "✓" : num}
                  </span>
                  <span
                    className={`dup-step-label${isActive ? " dup-step-label--active" : ""}`}
                  >
                    {label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div
                    className={`dup-step-line${isDone ? " dup-step-line--done" : ""}`}
                    aria-hidden="true"
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Body ── */}
        <div className="dup-body">
          {/* ════ STEP 1: Details ════ */}
          {step === 1 && (
            <div>
              <Field label="Project Title" required>
                <input
                  className="dup-input"
                  type="text"
                  value={form.projectTitle}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, projectTitle: e.target.value }))
                  }
                  placeholder="New project title"
                  autoFocus
                  aria-required="true"
                />
              </Field>
              <Field label="Description">
                <textarea
                  className="dup-input dup-input--textarea"
                  value={form.description}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, description: e.target.value }))
                  }
                  rows={3}
                  placeholder="Optional description"
                />
              </Field>
              <div className="dup-row">
                <Field label="Start Date">
                  <input
                    className="dup-input"
                    type="date"
                    value={form.startDate}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, startDate: e.target.value }))
                    }
                  />
                </Field>
                <Field label="End Date">
                  <input
                    className="dup-input"
                    type="date"
                    value={form.endDate}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, endDate: e.target.value }))
                    }
                  />
                </Field>
              </div>
            </div>
          )}

          {/* ════ STEP 2: Tasks ════ */}
          {step === 2 && (
            <div>
              <div className="dup-status-bar">
                <div className="dup-status-bar__left">
                  {isDuplicateAll ? (
                    <span className="dup-badge dup-badge--all">
                      ⧉ Duplicating everything
                    </span>
                  ) : (
                    <span className="dup-badge dup-badge--partial">
                      ◈ Partial selection
                    </span>
                  )}
                  <span className="dup-count-summary">
                    {selectedCount.tasks}/{totalCount.tasks} tasks
                    {totalCount.todos > 0 &&
                      ` · ${selectedCount.todos}/${totalCount.todos} to-dos`}
                    {totalCount.lists > 0 &&
                      ` · ${selectedCount.lists}/${totalCount.lists} checklist items`}
                  </span>
                </div>
                <div className="dup-status-bar__right">
                  <button
                    className="dup-link-btn"
                    onClick={handleSelectAll}
                    type="button"
                  >
                    Select all
                  </button>
                  <span className="dup-divider" aria-hidden="true">
                    ·
                  </span>
                  <button
                    className="dup-link-btn"
                    onClick={handleDeselectAll}
                    type="button"
                  >
                    Deselect all
                  </button>
                </div>
              </div>

              <div className="dup-tree" role="tree" aria-label="Task selection">
                {tasks.length === 0 && (
                  <p className="dup-empty">No tasks in this project.</p>
                )}
                {tasks.map((task) => {
                  const taskId = task.taskId ?? task.id;
                  const tsel = selectedTasks[taskId];
                  if (!tsel) return null;
                  const todos = task.todoRequestDTOList || [];
                  return (
                    <div
                      key={taskId}
                      className={`dup-task-node${tsel.checked ? " dup-task-node--checked" : " dup-task-node--unchecked"}`}
                      role="treeitem"
                      aria-selected={tsel.checked}
                      aria-expanded={tsel.checked && todos.length > 0}
                    >
                      <label className="dup-check-row">
                        <input
                          type="checkbox"
                          checked={tsel.checked}
                          onChange={() => toggleTask(taskId)}
                          className="dup-checkbox"
                          aria-label={`Select task: ${task.taskTitle || taskId}`}
                        />

                        <span className="dup-node-title dup-node-title--task">
                          {task.taskTitle || task.title || `Task #${taskId}`}
                        </span>
                        {todos.length > 0 && (
                          <span className="dup-count-badge dup-count-badge--todo">
                            {
                              todos.filter(
                                (td) => tsel.todos[td.toDoId ?? td.id]?.checked,
                              ).length
                            }
                            /{todos.length} to-dos
                          </span>
                        )}
                      </label>

                      {todos.length > 0 && (
                        <div
                          className={`dup-todo-list${!tsel.checked ? " dup-todo-list--disabled" : ""}`}
                          role="group"
                        >
                          {todos.map((todo) => {
                            const todoId = todo.toDoId ?? todo.id;
                            const tdsel = tsel.todos[todoId];
                            if (!tdsel) return null;
                            const lists = todo.toDoListResponseDTOList || [];
                            return (
                              <div
                                key={todoId}
                                className="dup-todo-node"
                                role="treeitem"
                                aria-selected={tdsel.checked}
                                aria-expanded={
                                  tdsel.checked && lists.length > 0
                                }
                              >
                                <label className="dup-check-row dup-check-row--todo">
                                  <input
                                    type="checkbox"
                                    checked={tdsel.checked}
                                    onChange={() => toggleTodo(taskId, todoId)}
                                    className="dup-checkbox"
                                    aria-label={`Select to-do: ${todo.toDoTitle || todoId}`}
                                  />

                                  <span className="dup-node-title">
                                    {todo.toDoTitle || `To-Do #${todoId}`}
                                  </span>
                                  {lists.length > 0 && (
                                    <span className="dup-count-badge dup-count-badge--list">
                                      {tdsel.lists.size}/{lists.length} items
                                    </span>
                                  )}
                                </label>

                                {lists.length > 0 && (
                                  <div
                                    className={`dup-list-items${!tdsel.checked ? " dup-list-items--disabled" : ""}`}
                                    role="group"
                                  >
                                    {lists.map((li) => {
                                      const listId = li.toDoListId ?? li.id;
                                      return (
                                        <label
                                          key={listId}
                                          className="dup-check-row dup-check-row--list-item"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={tdsel.lists.has(listId)}
                                            onChange={() =>
                                              toggleList(taskId, todoId, listId)
                                            }
                                            className="dup-checkbox"
                                            aria-label={`Select checklist item: ${li.toDoListTitle || listId}`}
                                          />

                                          <span className="dup-node-title dup-node-title--list-item">
                                            {li.toDoListTitle ||
                                              li.title ||
                                              `Item #${listId}`}
                                          </span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ════ STEP 3: Members ════ */}
          {step === 3 && (
            <div>
              <ToggleRow
                label="Copy existing members"
                sub="Bring all project members across (excluding you)"
                checked={copyEmployees}
                onToggle={() => setCopyEmployees((v) => !v)}
              />

              {copyEmployees ? (
                <div className="dup-members-preview">
                  <p className="dup-members-note">
                    The following members will be copied with their current
                    roles:
                  </p>
                  {otherMembers.length === 0 ? (
                    <p className="dup-empty">
                      No other members in this project.
                    </p>
                  ) : (
                    <div className="dup-chip-grid">
                      {otherMembers.map((m) => (
                        <MemberChip key={m.empId} member={m} />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="dup-manual-members">
                  <p className="dup-members-note">
                    Select members to add and assign their roles:
                    <span className="dup-member-hint">
                      {" "}
                      — existing members marked with ●
                    </span>
                  </p>

                  {loadingEmployees ? (
                    <div className="dup-loading-row">
                      <span className="dup-spinner" aria-hidden="true" />
                      Loading employees…
                    </div>
                  ) : employeesForManual.length === 0 ? (
                    <p className="dup-empty">No employees available.</p>
                  ) : (
                    <div className="dup-employee-list">
                      {employeesForManual.map((emp) => {
                        const empId = emp.empId;
                        const state = manualMembers[empId] || {
                          included: false,
                          roleId: 3,
                        };
                        const isMember = isProjectMember(empId);
                        return (
                          <div
                            key={empId}
                            className={`dup-manual-row${state.included ? " dup-manual-row--included" : ""}`}
                          >
                            <label className="dup-manual-check">
                              <input
                                type="checkbox"
                                checked={state.included}
                                onChange={(e) =>
                                  setManualMembers((prev) => ({
                                    ...prev,
                                    [empId]: {
                                      ...state,
                                      included: e.target.checked,
                                    },
                                  }))
                                }
                                className="dup-checkbox"
                              />
                              <Avatar name={emp.empName || "?"} />
                              <div className="dup-emp-info">
                                <span className="dup-emp-name">
                                  {emp.empName || `Emp #${empId}`}
                                  {isMember && (
                                    <span
                                      className="dup-member-dot"
                                      title="Existing project member"
                                      aria-label="Existing project member"
                                    >
                                      {" "}
                                      ●
                                    </span>
                                  )}
                                </span>
                                {(emp.roleName || emp.departmentName) && (
                                  <span className="dup-emp-meta">
                                    {[emp.roleName, emp.departmentName]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </span>
                                )}
                              </div>
                            </label>

                            {state.included && (
                              <select
                                className="dup-role-select"
                                value={state.roleId}
                                onChange={(e) =>
                                  setManualMembers((prev) => ({
                                    ...prev,
                                    [empId]: {
                                      ...state,
                                      roleId: Number(e.target.value),
                                    },
                                  }))
                                }
                                aria-label={`Role for ${emp.empName}`}
                              >
                                {availableRoles.map((r) => (
                                  <option key={r.roleId} value={r.roleId}>
                                    {r.roleType}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {Object.values(manualMembers).some((v) => v.included) && (
                    <div className="dup-selection-summary">
                      <span className="dup-selection-count">
                        {
                          Object.values(manualMembers).filter((v) => v.included)
                            .length
                        }{" "}
                        member
                        {Object.values(manualMembers).filter((v) => v.included)
                          .length !== 1
                          ? "s"
                          : ""}{" "}
                        selected
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {error && (
            <p className="dup-error-msg" role="alert">
              ⚠ {error}
            </p>
          )}
        </div>

        {/* ── Footer ── */}
        <footer className="dup-footer">
          <button
            className="dup-btn dup-btn--ghost"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <div className="dup-footer__right">
            {step > 1 && (
              <button
                className="dup-btn dup-btn--secondary"
                onClick={() => setStep((s) => s - 1)}
                disabled={loading}
              >
                ← Back
              </button>
            )}
            {step < 3 ? (
              <button className="dup-btn dup-btn--primary" onClick={handleNext}>
                Next →
              </button>
            ) : (
              <button
                className="dup-btn dup-btn--primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <span className="dup-spinner" aria-label="Duplicating…" />
                ) : (
                  <>⧉ Duplicate Project{isDuplicateAll ? "" : " (partial)"}</>
                )}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function Field({ label, required, children }) {
  return (
    <div className="dup-field">
      <label className="dup-field-label">
        {label}
        {required && (
          <span className="dup-required" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

function ToggleRow({ label, sub, checked, onToggle }) {
  return (
    <div className="dup-toggle-row">
      <div className="dup-toggle-row__label">
        <span className="dup-toggle-row__title">{label}</span>
        <span className="dup-toggle-row__sub">{sub}</span>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        className={`dup-toggle-btn${checked ? " dup-toggle-btn--on" : " dup-toggle-btn--off"}`}
        onClick={onToggle}
      >
        <span
          className="dup-toggle-knob"
          style={{
            transform: checked ? "translateX(18px)" : "translateX(2px)",
          }}
        />
      </button>
    </div>
  );
}

function Avatar({ name }) {
  return (
    <div className="dup-avatar" aria-hidden="true">
      {(name || "?").charAt(0).toUpperCase()}
    </div>
  );
}

function MemberChip({ member }) {
  return (
    <div className="dup-member-chip">
      <Avatar name={member.empName || "?"} />
      <div>
        <div className="dup-member-chip__name">{member.empName}</div>
        <div className="dup-member-chip__role">
          {member.roleType || member.accessName || ""}
        </div>
      </div>
    </div>
  );
}
